/**
 * Life Story Question Bank
 * 
 * Each section contains questions with optional follow-up prompts.
 * Questions are designed for conversational interviews with seniors.
 */

export const QUESTION_SECTIONS = {
  consent: {
    id: 'consent',
    title: 'Welcome & Consent',
    description: 'Required before starting the life story',
    required: true,
    icon: '✋',
    questions: [
      {
        id: 'consent_1',
        question: "Hi {name}, we'd love to capture some of your life stories to share with your family and our team here. Is that okay with you?",
        followUp: null,
        isConsent: true,
        consentType: 'verbal'
      },
      {
        id: 'consent_2', 
        question: "Some of your stories might be shared on screens around the community or used to help staff get to know you better. Is that alright?",
        followUp: "You can always change your mind later.",
        isConsent: true,
        consentType: 'public_sharing'
      },
      {
        id: 'intro_1',
        question: "Before we dive in—what name do you prefer to be called?",
        followUp: null,
        isConsent: false,
        capturesPreferredName: true
      }
    ]
  },

  early: {
    id: 'early',
    title: 'Early Life & Childhood',
    description: 'Where it all began',
    required: false,
    icon: '👶',
    questions: [
      {
        id: 'early_1',
        question: "Where were you born, and what was it like growing up there?",
        followUp: "Describe your neighborhood. What do you miss about it?"
      },
      {
        id: 'early_2',
        question: "What's your earliest memory?",
        followUp: null
      },
      {
        id: 'early_3',
        question: "What were you like as a child?",
        followUp: "Were you shy or outgoing? A troublemaker or rule-follower?"
      },
      {
        id: 'early_4',
        question: "What did you do for fun as a kid?",
        followUp: "Games, outdoor activities, hobbies?"
      },
      {
        id: 'early_5',
        question: "Did you have a childhood pet?",
        followUp: null
      },
      {
        id: 'early_6',
        question: "What was your favorite thing to eat growing up? Who made it?",
        followUp: null
      },
      {
        id: 'early_7',
        question: "What holidays or traditions were special in your family?",
        followUp: null
      },
      {
        id: 'early_lang',
        question: "What language did you grow up speaking at home?",
        followUp: "Do you speak any other languages?",
        ecpFields: ['general.primaryLanguage']
      }
    ]
  },

  family: {
    id: 'family',
    title: 'Family & Heritage',
    description: 'The people who shaped you',
    required: false,
    icon: '👨‍👩‍👧‍👦',
    questions: [
      {
        id: 'family_1',
        question: "Tell me about your parents. What were they like?",
        followUp: "What did they do for work?"
      },
      {
        id: 'family_2',
        question: "Do you have brothers or sisters? What are some memories with them?",
        followUp: null
      },
      {
        id: 'family_3',
        question: "Were there other relatives who were important to you growing up?",
        followUp: "Grandparents, aunts, uncles?"
      },
      {
        id: 'family_4',
        question: "What's your family's heritage or background?",
        followUp: "Where did your family come from originally?"
      },
      {
        id: 'family_5',
        question: "Are there any family traditions you've carried forward?",
        followUp: null
      },
      {
        id: 'family_6',
        question: "Is there a family recipe that's been passed down?",
        followUp: null
      },
      {
        id: 'family_heritage_bg',
        question: "How would you describe your cultural or ethnic background?",
        followUp: "Where did your family originally come from?",
        ecpFields: ['general.race', 'general.heritage']
      },
      {
        id: 'family_pets_now',
        question: "Have pets been part of your life? Any here with you now, or ones you still think about?",
        followUp: "What were their names?",
        ecpFields: ['family.pets']
      }
    ]
  },

  education: {
    id: 'education',
    title: 'Education & School Years',
    description: 'Learning and growing up',
    required: false,
    icon: '🎓',
    questions: [
      {
        id: 'edu_1',
        question: "Where did you go to school?",
        followUp: null
      },
      {
        id: 'edu_2',
        question: "What subjects did you enjoy most?",
        followUp: null
      },
      {
        id: 'edu_3',
        question: "Did you have a favorite teacher who made an impact on you?",
        followUp: null
      },
      {
        id: 'edu_4',
        question: "Were you involved in any sports, clubs, or activities?",
        followUp: null
      },
      {
        id: 'edu_5',
        question: "What's a fun memory from your school days?",
        followUp: null
      },
      {
        id: 'edu_6',
        question: "Did you go to college or trade school? What did you study?",
        followUp: null
      }
    ]
  },

  career: {
    id: 'career',
    title: 'Career & Work Life',
    description: "Your life's work",
    required: false,
    icon: '💼',
    questions: [
      {
        id: 'career_1',
        question: "What was your first job?",
        followUp: "How old were you? How much did you earn?"
      },
      {
        id: 'career_2',
        question: "What kind of work did you do for most of your career?",
        followUp: null
      },
      {
        id: 'career_3',
        question: "What did you enjoy most about your work?",
        followUp: null
      },
      {
        id: 'career_4',
        question: "Is there an accomplishment from your career you're especially proud of?",
        followUp: null
      },
      {
        id: 'career_5',
        question: "Did you have any mentors who helped you along the way?",
        followUp: null
      },
      {
        id: 'career_6',
        question: "If you could give career advice to a young person today, what would it be?",
        followUp: null
      }
    ]
  },

  love: {
    id: 'love',
    title: 'Love & Marriage',
    description: 'Matters of the heart',
    required: false,
    icon: '💕',
    questions: [
      {
        id: 'love_1',
        question: "How did you meet your spouse or partner?",
        followUp: null
      },
      {
        id: 'love_2',
        question: "What was your first impression of them?",
        followUp: null
      },
      {
        id: 'love_3',
        question: "Tell me about your wedding day.",
        followUp: "Where was it? Who was there?"
      },
      {
        id: 'love_4',
        question: "What's the secret to a good relationship?",
        followUp: null
      },
      {
        id: 'love_5',
        question: "What's a favorite memory with your spouse or partner?",
        followUp: null
      },
      {
        id: 'love_status',
        question: "Are you married, or were you? Tell me a little about that.",
        followUp: "What's their name?",
        ecpFields: ['general.maritalStatus', 'general.spouseName']
      }
    ]
  },

  children: {
    id: 'children',
    title: 'Children & Grandchildren',
    description: 'Your legacy',
    required: false,
    icon: '👨‍👧‍👦',
    questions: [
      {
        id: 'kids_1',
        question: "Tell me about your children.",
        followUp: "Their names, something special about each?"
      },
      {
        id: 'kids_2',
        question: "What's a favorite memory from when your kids were young?",
        followUp: null
      },
      {
        id: 'kids_3',
        question: "Tell me about your grandchildren or great-grandchildren.",
        followUp: null
      },
      {
        id: 'kids_4',
        question: "What do you hope your grandchildren remember about you?",
        followUp: null
      },
      {
        id: 'kids_5',
        question: "What values did you try to pass on to your family?",
        followUp: null
      }
    ]
  },

  hobbies: {
    id: 'hobbies',
    title: 'Interests & Hobbies',
    description: 'What brings you joy',
    required: false,
    icon: '🎨',
    questions: [
      {
        id: 'hobby_1',
        question: "What hobbies have you enjoyed over the years?",
        followUp: null
      },
      {
        id: 'hobby_2',
        question: "Is there something you could talk about for hours?",
        followUp: null
      },
      {
        id: 'hobby_3',
        question: "Did you play any sports or stay active?",
        followUp: null
      },
      {
        id: 'hobby_4',
        question: "Do you enjoy music? What kind? Any favorite artists?",
        followUp: null
      },
      {
        id: 'hobby_5',
        question: "Do you like to read? What kinds of books?",
        followUp: null
      },
      {
        id: 'hobby_6',
        question: "Have you traveled anywhere memorable?",
        followUp: "Favorite trip? Dream destination?"
      },
      {
        id: 'hobby_7',
        question: "Do you have a favorite movie or TV show?",
        followUp: null
      }
    ]
  },

  favorites: {
    id: 'favorites',
    title: 'Favorites & Preferences',
    description: 'The little things that matter',
    required: false,
    icon: '⭐',
    questions: [
      {
        id: 'fav_1',
        question: "What's your favorite food?",
        followUp: null
      },
      {
        id: 'fav_2',
        question: "Do you have a favorite dessert or treat?",
        followUp: null
      },
      {
        id: 'fav_3',
        question: "Coffee or tea? How do you take it?",
        followUp: null
      },
      {
        id: 'fav_4',
        question: "What's your favorite time of day?",
        followUp: "Morning person or night owl?"
      },
      {
        id: 'fav_5',
        question: "What's your favorite season and why?",
        followUp: null
      },
      {
        id: 'fav_6',
        question: "Is there a song that always makes you happy?",
        followUp: null
      },
      {
        id: 'fav_7',
        question: "Do you have a favorite color?",
        followUp: null
      },
      {
        id: 'fav_food_dislikes',
        question: "Are there any foods you really don't care for?",
        followUp: null,
        ecpFields: ['dining.dislikes']
      }
    ]
  },

  dailyRhythm: {
    id: 'dailyRhythm',
    title: 'Daily Rhythm & Comfort',
    description: 'How you like your days and evenings to feel',
    required: false,
    icon: '🌙',
    questions: [
      {
        id: 'rhythm_1',
        question: "Are you more of a morning person or an evening person?",
        followUp: "When do you feel most like yourself during the day?",
        ecpFields: ['activity.preferredTime']
      },
      {
        id: 'rhythm_2',
        question: "What time do you usually like to wake up, and how do you like to start your day?",
        followUp: "Slowly with coffee, or up and going?",
        ecpFields: ['sleep.wakeTime', 'sleep.wakeStyle']
      },
      {
        id: 'rhythm_3',
        question: "What time do you usually head to bed?",
        followUp: "Do you have a little routine before sleep?",
        ecpFields: ['sleep.bedtime', 'sleep.routine']
      },
      {
        id: 'rhythm_4',
        question: "What helps you feel cozy and settled at night?",
        followUp: "An extra blanket, a certain light, quiet or some soft sound?",
        ecpFields: ['sleep.comfort', 'sleep.lightNoise']
      },
      {
        id: 'rhythm_5',
        question: "Do you enjoy a rest or a nap during the day?",
        followUp: null,
        ecpFields: ['sleep.naps']
      },
      {
        id: 'rhythm_6',
        question: "Do you prefer a bath or a shower — and morning or evening?",
        followUp: null,
        ecpFields: ['personal.bathing']
      },
      {
        id: 'rhythm_7',
        question: "Is there a snack or drink you enjoy in the evening?",
        followUp: null,
        ecpFields: ['dining.eveningSnack']
      },
      {
        id: 'rhythm_8',
        question: "What does a really good, perfect day look like for you?",
        followUp: "From morning to night — what would make it wonderful?",
        ecpFields: ['values.perfectDay']
      }
    ]
  },

  faith: {
    id: 'faith',
    title: 'Faith & Spirituality',
    description: 'What gives you meaning',
    required: false,
    optional: true,
    icon: '🙏',
    questions: [
      {
        id: 'faith_1',
        question: "Did faith or spirituality play a role in your life?",
        followUp: null
      },
      {
        id: 'faith_2',
        question: "Were there any religious traditions important to your family?",
        followUp: null
      },
      {
        id: 'faith_3',
        question: "Is there a verse, prayer, or saying that's meaningful to you?",
        followUp: null
      }
    ]
  },

  military: {
    id: 'military',
    title: 'Military Service',
    description: 'Your service to country',
    required: false,
    conditional: true,
    icon: '🎖️',
    questions: [
      {
        id: 'military_1',
        question: "Which branch did you serve in, and when?",
        followUp: null
      },
      {
        id: 'military_2',
        question: "Where were you stationed?",
        followUp: null
      },
      {
        id: 'military_3',
        question: "What did you do in the service?",
        followUp: null
      },
      {
        id: 'military_4',
        question: "Are there memories from your service you'd like to share?",
        followUp: null
      },
      {
        id: 'military_5',
        question: "How did your service shape who you are?",
        followUp: null
      }
    ]
  },

  wisdom: {
    id: 'wisdom',
    title: 'Life Wisdom & Reflection',
    description: 'Lessons learned along the way',
    required: false,
    icon: '💡',
    questions: [
      {
        id: 'wisdom_1',
        question: "What are you most proud of in your life?",
        followUp: null
      },
      {
        id: 'wisdom_2',
        question: "What's the best advice you've ever received?",
        followUp: "Who gave it to you?"
      },
      {
        id: 'wisdom_3',
        question: "What's a lesson life has taught you?",
        followUp: null
      },
      {
        id: 'wisdom_4',
        question: "If you could go back and tell your younger self one thing, what would it be?",
        followUp: null
      },
      {
        id: 'wisdom_5',
        question: "What do you know now that you wish you'd known at 25?",
        followUp: null
      }
    ]
  },

  inspiration: {
    id: 'inspiration',
    title: 'Guidance & Inspiration',
    description: 'Your message to others',
    required: false,
    icon: '✨',
    questions: [
      {
        id: 'inspire_1',
        question: "What advice would you give to young people just starting out in life?",
        followUp: null
      },
      {
        id: 'inspire_2',
        question: "What would you tell someone going through a difficult time?",
        followUp: "How did you get through hard times?"
      },
      {
        id: 'inspire_3',
        question: "What do you think is the key to a happy life?",
        followUp: null
      },
      {
        id: 'inspire_4',
        question: "Is there something you wish more people understood about getting older?",
        followUp: null
      },
      {
        id: 'inspire_5',
        question: "What message would you want to leave for your grandchildren or great-grandchildren?",
        followUp: null
      },
      {
        id: 'inspire_6',
        question: "If you could write a letter to the world, what would you say?",
        followUp: null
      }
    ]
  },

  future: {
    id: 'future',
    title: 'Looking Ahead',
    description: 'Hopes, dreams, and the road ahead',
    required: false,
    icon: '🌅',
    questions: [
      {
        id: 'future_1',
        question: "What are you looking forward to right now?",
        followUp: "This week, this year, or beyond?"
      },
      {
        id: 'future_2',
        question: "Is there something you still want to learn or try?",
        followUp: null
      },
      {
        id: 'future_3',
        question: "Is there a place you'd still love to visit or revisit?",
        followUp: null
      },
      {
        id: 'future_4',
        question: "What gives you hope when you think about the future?",
        followUp: null
      },
      {
        id: 'future_5',
        question: "What are your hopes for your family in the years ahead?",
        followUp: null
      },
      {
        id: 'future_6',
        question: "What do you hope the world looks like for future generations?",
        followUp: null
      },
      {
        id: 'future_7',
        question: "Is there something you're still dreaming about?",
        followUp: "A goal, an experience, a connection?"
      },
      {
        id: 'future_8',
        question: "What's something that makes you excited to wake up in the morning?",
        followUp: null
      },
      {
        id: 'future_9',
        question: "If you could witness one thing in the future, what would it be?",
        followUp: "A family milestone, a world event, a personal achievement?"
      }
    ]
  },

  closing: {
    id: 'closing',
    title: 'Final Thoughts',
    description: 'Wrapping up your story',
    required: false,
    icon: '📖',
    questions: [
      {
        id: 'closing_1',
        question: "How would you like to be remembered?",
        followUp: null
      },
      {
        id: 'closing_2',
        question: "Is there anything else you'd like people to know about you?",
        followUp: null
      },
      {
        id: 'closing_3',
        question: "Is there a story we didn't get to that you'd like to share?",
        followUp: null
      }
    ]
  }
};

// Ordered array of section IDs for navigation
export const SECTION_ORDER = [
  'consent',
  'early',
  'family', 
  'education',
  'career',
  'love',
  'children',
  'hobbies',
  'favorites',
  'dailyRhythm',
  'faith',
  'military',
  'wisdom',
  'inspiration',
  'future',
  'closing'
];

// Get total question count (excluding consent)
export const getTotalQuestionCount = () => {
  return Object.values(QUESTION_SECTIONS)
    .filter(section => section.id !== 'consent')
    .reduce((total, section) => total + section.questions.length, 0);
};

// Get questions for a specific section
export const getSection = (sectionId) => {
  return QUESTION_SECTIONS[sectionId] || null;
};

// Get a specific question by ID
export const getQuestion = (questionId) => {
  for (const section of Object.values(QUESTION_SECTIONS)) {
    const question = section.questions.find(q => q.id === questionId);
    if (question) {
      return { ...question, section: section.id };
    }
  }
  return null;
};

// Get next unanswered question
export const getNextUnanswered = (answeredIds = [], skippedSections = []) => {
  for (const sectionId of SECTION_ORDER) {
    // Skip over skipped sections
    if (skippedSections.includes(sectionId)) continue;
    
    const section = QUESTION_SECTIONS[sectionId];
    for (const question of section.questions) {
      if (!answeredIds.includes(question.id)) {
        return { ...question, section: sectionId };
      }
    }
  }
  return null; // All questions answered
};

// Calculate completion percentage
export const getCompletionPercentage = (answeredIds = []) => {
  const totalQuestions = getTotalQuestionCount();
  const answeredCount = answeredIds.filter(id => !id.startsWith('consent_') && id !== 'intro_1').length;
  return Math.min(100, Math.round((answeredCount / totalQuestions) * 100));
};

// Get all questions in order (flattened)
export const getAllQuestions = () => {
  const questions = [];
  for (const sectionId of SECTION_ORDER) {
    const section = QUESTION_SECTIONS[sectionId];
    for (const question of section.questions) {
      questions.push({ ...question, section: sectionId });
    }
  }
  return questions;
};

// Get section totals for progress calculation
export const SECTION_QUESTION_COUNTS = {
  early: 8,
  family: 8,
  education: 6,
  career: 6,
  love: 6,
  children: 5,
  hobbies: 7,
  favorites: 8,
  dailyRhythm: 8,
  faith: 3,
  military: 5,
  wisdom: 5,
  inspiration: 6,
  future: 9,
  closing: 3
};

export default QUESTION_SECTIONS;
